import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { useUserStore } from '@/stores/user'
import { fetchOrgTree } from '@/api/org'
import { fetchTasksByExecutor } from '@/api/task'

function normalizeTask(t) {
  if (!t) return t
  return {
    id: t.id,
    taskNo: t.taskNo,
    title: t.title,
    projectId: t.projectId,
    projectName: t.projectName || '',
    creator: t.creatorName || t.creator || '',
    creatorUserId: t.creatorUserId,
    executor: t.executorName || t.executor || '',
    executorUserId: t.executorUserId,
    createdAt: t.createdAt,
    desc: t.description ?? t.desc ?? '',
    priority: (t.priority || '').toLowerCase(),
    dates: (t.scheduledDates || t.dates || []).map((d) =>
      typeof d === 'string' && d.length > 10 ? d.substring(0, 10) : d
    ),
    plannedStartDate: t.plannedStartDate || '',
    plannedEndDate: t.plannedEndDate || '',
    status: (t.status || '').toLowerCase(),
    taskType: (t.taskType || 'new').toLowerCase(),
    supplements: (t.supplements || []).map((s) => ({
      id: s.id,
      content: s.content,
      author: s.authorName || s.author || '',
      createdAt: s.createdAt,
    })),
    logs: (t.logs || []).map((l) => ({
      id: l.id,
      text: l.action,
      createdAt: l.createdAt,
    })),
  }
}

export const useScheduleStore = defineStore('schedule', () => {
  const orgTree = ref([])
  const selectedKey = ref('')
  const selectedType = ref('person')
  const viewMode = ref('calendar')
  const calendarYear = ref(new Date().getFullYear())
  const calendarMonth = ref(new Date().getMonth())
  const searchQuery = ref('')
  const filteredTasks = ref([])
  const loading = ref(false)

  async function init() {
    try {
      const data = await fetchOrgTree()
      orgTree.value = (data || []).map((dept) => ({
        key: `dept-${dept.id}`,
        label: dept.name,
        type: 'department',
        deptId: dept.id,
        children: (dept.users || []).map((u) => ({
          key: `user-${u.id}`,
          label: u.displayName || u.username,
          type: 'person',
          userId: u.id,
          department: dept.name,
          deptKey: `dept-${dept.id}`,
          deptLabel: dept.name,
        })),
      }))
    } catch {
      orgTree.value = []
    }
  }

  function getPersonByKey(key) {
    for (const dept of orgTree.value) {
      const person = dept.children?.find((c) => c.key === key)
      if (person) return { ...person, deptKey: dept.key, deptLabel: dept.label }
    }
    return null
  }

  function getDeptByKey(key) {
    return orgTree.value.find((d) => d.key === key) ?? null
  }

  function getDeptMemberNames(deptKey) {
    const dept = orgTree.value.find((d) => d.key === deptKey)
    return dept?.children?.map((c) => c.label) ?? []
  }

  function findPersonKeyByName(name) {
    for (const dept of orgTree.value) {
      const person = dept.children?.find((c) => c.label === name)
      if (person) return person.key
    }
    return null
  }

  function buildTreeOptions(filter = '') {
    const q = filter.trim().toLowerCase()
    return orgTree.value
      .map((dept) => {
        const members = dept.children ?? []
        const matchedMembers = q
          ? members.filter(
              (m) =>
                m.label.toLowerCase().includes(q) || dept.label.toLowerCase().includes(q)
            )
          : members
        const deptMatch = q && dept.label.toLowerCase().includes(q)
        if (q && !deptMatch && !matchedMembers.length) return null
        return {
          key: dept.key,
          label: `${dept.label} (${members.length})`,
          type: 'department',
          children: (deptMatch ? members : matchedMembers).map((m) => ({
            key: m.key,
            label: m.label,
            type: 'person',
            isLeaf: true,
          })),
        }
      })
      .filter(Boolean)
  }

  async function loadFilteredTasks() {
    if (!selectedKey.value) {
      filteredTasks.value = []
      return
    }
    loading.value = true
    try {
      if (selectedType.value === 'person') {
        const person = getPersonByKey(selectedKey.value)
        if (!person?.userId) {
          filteredTasks.value = []
          return
        }
        const data = await fetchTasksByExecutor(person.userId)
        filteredTasks.value = (data || []).map(normalizeTask)
      } else if (selectedType.value === 'department') {
        const dept = getDeptByKey(selectedKey.value)
        if (!dept?.children?.length) {
          filteredTasks.value = []
          return
        }
        const results = await Promise.all(
          dept.children.map((m) =>
            fetchTasksByExecutor(m.userId).catch(() => [])
          )
        )
        const seen = new Set()
        filteredTasks.value = results
          .flat()
          .map(normalizeTask)
          .filter((t) => {
            if (seen.has(t.id)) return false
            seen.add(t.id)
            return true
          })
      } else {
        filteredTasks.value = []
      }
    } catch {
      filteredTasks.value = []
    } finally {
      loading.value = false
    }
  }

  watch(
    () => [selectedKey.value, selectedType.value],
    ([key]) => {
      if (key) loadFilteredTasks()
    }
  )

  const isDeptView = computed(() => selectedType.value === 'department')

  const contextLabel = computed(() => {
    const count = filteredTasks.value.length
    if (selectedType.value === 'person') {
      const person = getPersonByKey(selectedKey.value)
      if (!person) return `· ${count} 项任务`
      return `${person.deptLabel} / ${person.label} · ${count} 项任务`
    }
    const dept = getDeptByKey(selectedKey.value)
    if (!dept) return `· ${count} 项任务`
    const memberCount = dept.children?.length ?? 0
    return `${dept.label} · ${memberCount} 人 · ${count} 项任务`
  })

  function selectNode(key, type) {
    selectedKey.value = key
    selectedType.value = type
  }

  function setViewMode(mode) {
    viewMode.value = mode
  }

  function initDefaultSelection() {
    const userStore = useUserStore()
    const key = findPersonKeyByName(userStore.displayName)
    if (key) {
      selectNode(key, 'person')
      return
    }
    const firstDept = orgTree.value[0]
    if (firstDept?.children?.[0]) {
      selectNode(firstDept.children[0].key, 'person')
    }
  }

  function changeMonth(delta) {
    let y = calendarYear.value
    let m = calendarMonth.value + delta
    if (m > 11) { m = 0; y++ }
    if (m < 0) { m = 11; y-- }
    calendarYear.value = y
    calendarMonth.value = m
  }

  function goToday() {
    const now = new Date()
    calendarYear.value = now.getFullYear()
    calendarMonth.value = now.getMonth()
  }

  function tasksOnDate(dateStr) {
    return filteredTasks.value.filter((t) => t.dates?.includes(dateStr))
  }

  return {
    orgTree,
    selectedKey,
    selectedType,
    viewMode,
    calendarYear,
    calendarMonth,
    searchQuery,
    filteredTasks,
    loading,
    isDeptView,
    contextLabel,
    getPersonByKey,
    getDeptByKey,
    getDeptMemberNames,
    findPersonKeyByName,
    buildTreeOptions,
    selectNode,
    setViewMode,
    initDefaultSelection,
    changeMonth,
    goToday,
    tasksOnDate,
    loadFilteredTasks,
    init,
  }
})
